import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CreditCard as TerminalIcon,
  Schedule as ScheduleIcon,
  LocationOn as LocationIcon,
  Battery3Bar as BatteryIcon,
  SignalWifi4Bar as SignalIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { collection, doc, updateDoc, deleteDoc, addDoc, query, where, orderBy, getDocs } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme as useMuiTheme } from '@mui/material/styles';
import { useTheme } from '../../contexts/ThemeContext';
import { 
  Terminal, 
  TerminalStatus, 
  TerminalSensitivity,
  getTerminalStatusColor,
  getTerminalStatusLabel
} from '../../types/terminal';
import { Report, convertTimestamp } from '../../types/firebase';
import { useFeatureFlag } from '../../config/featureFlags';
import { includePublicReportInSharedViews } from '../../utils/reportPrivacyDisplay';

const TerminalCard = styled(Card)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  marginBottom: '12px',
  borderRadius: '10px',
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
    transform: 'translateY(-2px)',
  },
}));

const StatusChip = styled(Chip)<{ terminalstatus: TerminalStatus }>(({ terminalstatus, theme }) => ({
  backgroundColor: getTerminalStatusColor(terminalstatus),
  color: theme.palette.primary.contrastText,
  fontWeight: 'bold',
}));

const ActionButton = styled(IconButton)(({ theme }) => ({
  backgroundColor: theme.palette.action.hover,
  color: theme.palette.text.secondary,
  margin: '0 4px',
  '&:hover': {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  },
}));

interface TerminalManagementProps {
  siteId: string;
  siteName: string;
  terminals: Terminal[];
  onTerminalsUpdate: (terminals: Terminal[]) => void;
}

interface AddTerminalModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (terminalData: any) => Promise<void>;
  siteId: string;
  editingTerminal?: Terminal | null;
}

const terminalTypes = [
  'ATM',
  'POS Terminal',
  'Card Reader',
  'Payment Terminal',
  'Self-Service Kiosk',
  'Fuel Pump',
  'Vending Machine',
  'Access Control Reader'
];

const terminalMakes = [
  'NCR',
  'Diebold Nixdorf',
  'Ingenico',
  'Verifone',
  'First Data',
  'Square',
  'PAX Technology',
  'Clover',
  'Other'
];

const commonTags = [
  'high-traffic',
  'outdoor',
  'indoor',
  'self-service',
  'attended',
  'cash-handling',
  'contactless',
  'chip-enabled',
  'legacy',
  'priority'
];

const AddTerminalModal: React.FC<AddTerminalModalProps> = ({ 
  open, 
  onClose, 
  onSave, 
  siteId, 
  editingTerminal 
}) => {
  const [formData, setFormData] = useState({
    name: '',
    terminalType: 'Card Reader',
    makeModel: '',
    readerModel: '',
    serialNumber: '',
    siteLocation: {
      description: '',
    },
    assignedUnitId: '',
    tags: [] as string[],
    scanDefaults: {
      duration: 30,
      sensitivity: 'medium' as TerminalSensitivity,
    },
    status: 'unscanned' as TerminalStatus,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const { userProfile } = useAuth();
  const { colors } = useTheme();
  const muiTheme = useMuiTheme();

  useEffect(() => {
    if (editingTerminal) {
      setFormData({
        name: editingTerminal.name || '',
        terminalType: editingTerminal.terminalType || 'Card Reader',
        makeModel: editingTerminal.makeModel || '',
        readerModel: editingTerminal.readerModel || '',
        serialNumber: editingTerminal.serialNumber || '',
        siteLocation: {
          description: editingTerminal.siteLocation?.description || '',
        },
        assignedUnitId: editingTerminal.assignedUnitId || '',
        tags: editingTerminal.tags || [],
        scanDefaults: {
          duration: editingTerminal.scanDefaults?.duration || 30,
          sensitivity: editingTerminal.scanDefaults?.sensitivity || 'medium',
        },
        status: editingTerminal.status || 'unscanned',
      });
    } else {
      setFormData({
        name: '',
        terminalType: 'Card Reader',
        makeModel: '',
        readerModel: '',
        serialNumber: '',
        siteLocation: {
          description: '',
        },
        assignedUnitId: '',
        tags: [],
        scanDefaults: {
          duration: 30,
          sensitivity: 'medium',
        },
        status: 'unscanned',
      });
    }
  }, [editingTerminal, open]);

  const handleInputChange = (field: string, value: any) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent as keyof typeof prev] as any,
          [child]: value,
        },
      }));
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
  };

  const generateSerialNumber = () => {
    const prefix = 'TERM-';
    const timestamp = Date.now().toString().slice(-6);
    const random = Math.random().toString(36).substring(2, 6).toUpperCase();
    return `${prefix}${timestamp}-${random}`;
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.serialNumber) {
      setError('Name and serial number are required');
      return;
    }

    try {
      setLoading(true);
      setError('');

      const terminalData = {
        ...formData,
        siteId,
        masterOwnerID: userProfile?.masterOwnerID,
        lastScannedAt: editingTerminal?.lastScannedAt || null,
        updatedAt: new Date(),
        ...(editingTerminal ? {} : { createdAt: new Date() }),
      };

      await onSave(terminalData);
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to save terminal');
    } finally {
      setLoading(false);
    }
  };

  const paperSx = {
    background: 'rgba(255, 255, 255, 0.03)',
    backdropFilter: 'blur(20px)',
    WebkitBackdropFilter: 'blur(20px)',
    border: '1px solid rgba(34, 211, 238, 0.25)',
    borderTop: '1px solid rgba(34, 211, 238, 0.5)',
    borderLeft: '1px solid rgba(34, 211, 238, 0.4)',
    borderRadius: '20px',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5), inset 0 1px 1px rgba(255, 255, 255, 0.05), inset 0 0 20px rgba(34, 211, 238, 0.05), 0 0 15px rgba(34, 211, 238, 0.1)',
  };
  const titleSx = {
    fontFamily: "'Inter', sans-serif",
    color: '#ffffff',
    fontSize: '15px',
    fontWeight: 700,
    letterSpacing: '0.3em',
    textTransform: 'uppercase' as const,
    padding: '20px 24px',
    borderBottom: '1px solid rgba(34, 211, 238, 0.2)',
    backgroundColor: 'transparent',
  };
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: paperSx }}>
      <DialogTitle sx={titleSx}>
        {editingTerminal ? 'Edit Terminal' : 'Add New Terminal'}
      </DialogTitle>
      <DialogContent sx={{ backgroundColor: 'transparent', padding: '24px' }}>
        {error && <Alert severity="error" sx={{ marginBottom: 2 }}>{error}</Alert>}
        
        <Box sx={{ marginTop: 1 }}>
          <Box sx={{ display: 'flex', gap: 2, marginBottom: 2 }}>
            <TextField
              fullWidth
              label="Terminal Name *"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              disabled={loading}
              placeholder="e.g., ATM #1, Main Checkout, Fuel Pump A"
            />
            <FormControl fullWidth>
              <InputLabel>Terminal Type</InputLabel>
              <Select
                value={formData.terminalType}
                onChange={(e) => handleInputChange('terminalType', e.target.value)}
                disabled={loading}
              >
                {terminalTypes.map(type => (
                  <MenuItem key={type} value={type}>{type}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          
          <Box sx={{ display: 'flex', gap: 2, marginBottom: 2 }}>
            <Autocomplete
              freeSolo
              options={terminalMakes}
              value={formData.makeModel}
              onChange={(_, value) => handleInputChange('makeModel', value || '')}
              onInputChange={(_, value) => handleInputChange('makeModel', value)}
              disabled={loading}
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  label="Make/Model"
                  placeholder="e.g., NCR SelfServ 80, Ingenico iCT250"
                />
              )}
            />
            <TextField
              fullWidth
              label="Reader Model"
              value={formData.readerModel}
              onChange={(e) => handleInputChange('readerModel', e.target.value)}
              disabled={loading}
              placeholder="Specific card reader model"
            />
          </Box>
          
          <Box sx={{ display: 'flex', gap: 2, marginBottom: 2 }}>
            <TextField
              fullWidth
              label="Serial Number *"
              value={formData.serialNumber}
              onChange={(e) => handleInputChange('serialNumber', e.target.value)}
              disabled={loading}
              placeholder="TERM-XXXXXX-XXXX"
            />
            <Button
              variant="outlined"
              onClick={() => handleInputChange('serialNumber', generateSerialNumber())}
              disabled={loading}
              sx={{ 
                minWidth: '120px',
                borderColor: colors.Gradient,
                color: colors.Gradient,
                '&:hover': { 
                  backgroundColor: colors.Gradient,
                  color: colors.Light_Shadow 
                }
              }}
            >
              Generate
            </Button>
          </Box>
          
          <TextField
            fullWidth
            label="Location Description"
            value={formData.siteLocation.description}
            onChange={(e) => handleInputChange('siteLocation.description', e.target.value)}
            disabled={loading}
            placeholder="e.g., Main Entrance, Checkout Lane 3, Pump Island A"
            sx={{ marginBottom: 2 }}
          />

          <Box sx={{ display: 'flex', gap: 2, marginBottom: 2 }}>
            <TextField
              fullWidth
              label="Assigned Unit ID"
              value={formData.assignedUnitId}
              onChange={(e) => handleInputChange('assignedUnitId', e.target.value)}
              disabled={loading}
              placeholder="SkimBuster hardware unit ID (optional)"
            />
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={formData.status}
                onChange={(e) => handleInputChange('status', e.target.value)}
                disabled={loading}
              >
                <MenuItem value="unscanned">Unscanned</MenuItem>
                <MenuItem value="clean">Clean</MenuItem>
                <MenuItem value="possible_risk">Possible Risk</MenuItem>
                <MenuItem value="under_investigation">Under Investigation</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <Autocomplete
            multiple
            freeSolo
            options={commonTags}
            value={formData.tags}
            onChange={(_, value) => handleInputChange('tags', value)}
            disabled={loading}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Tags"
                placeholder="Add tags to categorize this terminal"
              />
            )}
            sx={{ marginBottom: 2 }}
          />
          
          <Typography variant="h6" color={muiTheme.palette.text.primary} sx={{ marginBottom: 2 }}>
            Scan Defaults
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 2, marginBottom: 2 }}>
            <TextField
              fullWidth
              label="Scan Duration (seconds)"
              type="number"
              value={formData.scanDefaults.duration}
              onChange={(e) => handleInputChange('scanDefaults.duration', parseInt(e.target.value))}
              disabled={loading}
              inputProps={{ min: 5, max: 300 }}
            />
            
            <FormControl fullWidth>
              <InputLabel>Detection Sensitivity</InputLabel>
              <Select
                value={formData.scanDefaults.sensitivity}
                onChange={(e) => handleInputChange('scanDefaults.sensitivity', e.target.value)}
                disabled={loading}
              >
                <MenuItem value="low">Low</MenuItem>
                <MenuItem value="medium">Medium</MenuItem>
                <MenuItem value="high">High</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ padding: '16px 24px', gap: 1, borderTop: '1px solid rgba(34, 211, 238, 0.2)', backgroundColor: 'transparent' }}>
        <Button
          onClick={onClose}
          disabled={loading}
          variant="outlined"
          sx={{
            color: '#ffffff',
            border: '1px solid rgba(255, 255, 255, 0.15)',
            background: 'rgba(255, 255, 255, 0.1)',
            borderRadius: '50px',
            fontWeight: 'bold',
            px: 2,
            '&:hover': { background: 'rgba(255, 255, 255, 0.2)', borderColor: 'rgba(255, 255, 255, 0.25)' },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={loading}
          variant="outlined"
          startIcon={loading && <CircularProgress size={16} />}
          sx={{
            color: '#ffffff',
            border: '1px solid rgba(34, 211, 238, 0.5)',
            background: 'rgba(34, 211, 238, 0.15)',
            borderRadius: '50px',
            fontWeight: 'bold',
            px: 2,
            '&:hover': { background: 'rgba(34, 211, 238, 0.25)', borderColor: '#22d3ee' },
            '&:disabled': { borderColor: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.5)', background: 'rgba(255,255,255,0.05)' },
          }}
        >
          {loading ? 'Saving...' : 'Save Terminal'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const TerminalManagement: React.FC<TerminalManagementProps> = ({ 
  siteId, 
  siteName, 
  terminals, 
  onTerminalsUpdate 
}) => {
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [editingTerminal, setEditingTerminal] = useState<Terminal | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [terminalToDelete, setTerminalToDelete] = useState<Terminal | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [reports, setReports] = useState<Report[]>([]);
  const [terminalScanCounts, setTerminalScanCounts] = useState<Record<string, number>>({});

  const { userProfile } = useAuth();
  const { colors } = useTheme();
  const muiTheme = useMuiTheme();
  const showHardwareVitals = useFeatureFlag('hardwareVitalsOnTerminal');

  // Fetch reports for this site
  useEffect(() => {
    const fetchReports = async () => {
      if (!userProfile || !siteId) return;

      try {
        const reportsCollection = collection(db, 'reports');
        let allReportsData: Report[] = [];
        
        if (userProfile.role === 'Clerk') {
          // Clerk users only see reports from same masterOwnerID and ownerID
          const privateQuery = query(
            reportsCollection,
            where('user.masterOwnerID', '==', userProfile.masterOwnerID),
            where('user.ownerID', '==', userProfile.ownerID),
            orderBy('createdAt', 'desc')
          );
          const privateSnapshot = await getDocs(privateQuery);
          allReportsData = privateSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
            createdAt: convertTimestamp(doc.data().createdAt),
          })) as Report[];
        } else {
          // Admin and Tech users see public reports and private reports from their masterOwnerID
          const publicQuery = query(
            reportsCollection,
            where('privacy', '==', 'public'),
            orderBy('createdAt', 'desc')
          );
          const publicSnapshot = await getDocs(publicQuery);
          const publicReports = publicSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
            createdAt: convertTimestamp(doc.data().createdAt),
          })) as Report[];

          const privateQuery = query(
            reportsCollection,
            where('privacy', '==', 'private'),
            where('user.masterOwnerID', '==', userProfile.masterOwnerID),
            orderBy('createdAt', 'desc')
          );
          const privateSnapshot = await getDocs(privateQuery);
          const privateReports = privateSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
            createdAt: convertTimestamp(doc.data().createdAt),
          })) as Report[];

          allReportsData = [...publicReports, ...privateReports];
        }

        allReportsData = allReportsData.filter(includePublicReportInSharedViews);

        // Filter reports for this specific site
        const siteReports = allReportsData.filter(report => {
          return report.readerDetails?.siteId === siteId ||
                 (report.readerDetails?.siteName && 
                  report.readerDetails.siteName.toLowerCase() === siteName.toLowerCase());
        });

        setReports(siteReports);
      } catch (err) {
        console.error('Error fetching reports:', err);
        setReports([]);
      }
    };

    fetchReports();
  }, [siteId, siteName, userProfile]);

  // Calculate scan counts for each terminal
  useEffect(() => {
    const calculateScanCounts = () => {
      const counts: Record<string, number> = {};
      
      terminals.forEach(terminal => {
        // Count reports that match this terminal by name or device properties
        const terminalReports = reports.filter(report => {
          const device = report.readerDetails?.device;
          if (!device) return false;
          
          // Match by terminal name using specificLocation (case insensitive)
          if (device.specificLocation && 
              device.specificLocation.toLowerCase().includes(terminal.name.toLowerCase())) {
            return true;
          }
          
          // Match by serial number using serialNumber property
          if (device.serialNumber && terminal.serialNumber &&
              device.serialNumber === terminal.serialNumber) {
            return true;
          }
          
          // Match by model name
          if (device.modelName && terminal.makeModel &&
              device.modelName.toLowerCase() === terminal.makeModel.toLowerCase()) {
            return true;
          }
          
          // Match by device type
          if (device.deviceType && terminal.terminalType &&
              device.deviceType.toLowerCase() === terminal.terminalType.toLowerCase()) {
            return true;
          }
          
          return false;
        });
        
        counts[terminal.id] = terminalReports.length;
      });
      
      setTerminalScanCounts(counts);
    };

    if (terminals.length > 0 && reports.length > 0) {
      calculateScanCounts();
    }
  }, [terminals, reports]);

  // Function to get display text for terminal status
  const getTerminalDisplayText = (terminal: Terminal): string => {
    const scanCount = terminalScanCounts[terminal.id] || 0;
    
    if (scanCount === 0) {
      return getTerminalStatusLabel(terminal.status);
    }
    
    return `${scanCount} scan${scanCount !== 1 ? 's' : ''}`;
  };

  const handleAddTerminal = async (terminalData: any) => {
    try {
      setLoading(true);
      console.log('Adding terminal (saving to devices collection for mobile compatibility):', terminalData);
      
      // Convert terminal data to the mobile app's device structure
      const deviceData = {
        // Map terminal fields to device fields based on Firebase structure
        deviceType: terminalData.terminalType || 'Card Reader',
        modelName: terminalData.makeModel || 'Unknown',
        modelNumber: terminalData.readerModel || '',
        specification: terminalData.siteLocation?.description || terminalData.name,
        qrCodeData: terminalData.serialNumber || '',
        
        // Include coordinates if available (can be added later)
        coordinates: {
          latitude: 0,
          longitude: 0
        },
        
        // Additional fields that might be used by mobile app
        batteryLevel: 95, // Default battery level
        wifiSignal: 80,   // Default wifi signal
        firmwareVersion: '2.1.4', // Default firmware
        isActive: terminalData.status === 'clean' || terminalData.status === 'unscanned',
        lastSeen: new Date(),
        
        // Keep terminal-specific data for web app compatibility
        terminalData: {
          name: terminalData.name,
          serialNumber: terminalData.serialNumber,
          tags: terminalData.tags || [],
          scanDefaults: terminalData.scanDefaults,
          status: terminalData.status,
          assignedUnitId: terminalData.assignedUnitId
        },
        
        // Common fields
        siteId,
        masterOwnerID: terminalData.masterOwnerID,
        createdAt: new Date(),
        updatedAt: new Date(),
      };

      const devicesRef = collection(db, `sites/${siteId}/devices`);
      const docRef = await addDoc(devicesRef, deviceData);
      
      console.log('Device added with ID (mobile compatible):', docRef.id);
      
      const newTerminal = {
        id: docRef.id,
        ...terminalData,
      };
      
      onTerminalsUpdate([...terminals, newTerminal]);
    } catch (err: any) {
      console.error('Error adding terminal:', err);
      setError('Failed to add terminal');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const handleEditTerminal = async (terminalData: any) => {
    if (!editingTerminal?.id || !siteId) return;

    try {
      setLoading(true);
      console.log('Updating terminal (saving to devices collection for mobile compatibility):', editingTerminal.id, terminalData);
      
      // Helper function to recursively remove undefined values
      const removeUndefinedValues = (obj: any): any => {
        if (obj === null || obj === undefined) return null;
        if (typeof obj !== 'object') return obj;
        if (Array.isArray(obj)) return obj.filter(item => item !== undefined).map(removeUndefinedValues);
        
        const cleaned: any = {};
        Object.keys(obj).forEach(key => {
          const value = obj[key];
          if (value !== undefined) {
            cleaned[key] = removeUndefinedValues(value);
          }
        });
        return cleaned;
      };
      
      // Convert terminal data to the mobile app's device structure
      const deviceUpdateData = {
        // Map terminal fields to device fields based on Firebase structure
        deviceType: terminalData.terminalType || '',
        modelName: terminalData.makeModel || '',
        modelNumber: terminalData.readerModel || '',
        specification: terminalData.siteLocation?.description || terminalData.name || '',
        qrCodeData: terminalData.serialNumber || '',
        
        // Update device status based on terminal status
        isActive: terminalData.status === 'clean' || terminalData.status === 'unscanned',
        lastSeen: new Date(),
        
        // Keep terminal-specific data for web app compatibility
        terminalData: {
          name: terminalData.name || '',
          serialNumber: terminalData.serialNumber || '',
          tags: terminalData.tags || [],
          scanDefaults: terminalData.scanDefaults || { duration: 30, sensitivity: 'medium' },
          status: terminalData.status || 'unscanned',
          assignedUnitId: terminalData.assignedUnitId || ''
        },
        
        updatedAt: new Date(),
      };
      
      // Remove undefined values recursively
      const cleanedData = removeUndefinedValues(deviceUpdateData);
      
      const deviceRef = doc(db, 'sites', siteId, 'devices', editingTerminal.id);
      await updateDoc(deviceRef, cleanedData);
      
      console.log('Device updated successfully (mobile compatible)');
      
      const updatedTerminals = terminals.map(terminal => 
        terminal.id === editingTerminal.id ? { ...terminal, ...terminalData } : terminal
      );
      
      onTerminalsUpdate(updatedTerminals);
      setEditingTerminal(null);
    } catch (err: any) {
      console.error('Error updating terminal:', err);
      setError('Failed to update terminal');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTerminal = async () => {
    if (!terminalToDelete?.id || !siteId) return;

    try {
      setLoading(true);
      console.log('Deleting terminal (from devices collection for mobile compatibility):', terminalToDelete.id);

      const deviceRef = doc(db, 'sites', siteId, 'devices', terminalToDelete.id);
      await deleteDoc(deviceRef);
      
      console.log('Device deleted successfully (mobile compatible)');
      
      const updatedTerminals = terminals.filter(terminal => terminal.id !== terminalToDelete.id);
      onTerminalsUpdate(updatedTerminals);
      
      setDeleteDialogOpen(false);
      setTerminalToDelete(null);
    } catch (err: any) {
      console.error('Error deleting terminal:', err);
      setError('Failed to delete terminal');
    } finally {
      setLoading(false);
    }
  };

  const openEditModal = (terminal: Terminal) => {
    setEditingTerminal(terminal);
    setAddModalOpen(true);
  };

  const openDeleteDialog = (terminal: Terminal) => {
    setTerminalToDelete(terminal);
    setDeleteDialogOpen(true);
  };

  const closeModal = () => {
    setAddModalOpen(false);
    setEditingTerminal(null);
  };

  const formatLastScanned = (lastScannedAt?: Date) => {
    if (!lastScannedAt) return '—';
    return new Date(lastScannedAt).toLocaleString();
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" marginBottom={3}>
        <Typography variant="h6" color={muiTheme.palette.text.primary}>
          Terminal Management
        </Typography>
        {userProfile?.role === 'Admin' && (
          <Button
            startIcon={<AddIcon />}
            variant="contained"
            onClick={() => setAddModalOpen(true)}
            sx={{
              backgroundColor: muiTheme.palette.primary.main,
              '&:hover': { backgroundColor: muiTheme.palette.primary.dark }
            }}
          >
            Add Terminal
          </Button>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ marginBottom: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {terminals.length === 0 ? (
        <Box textAlign="center" padding={4}>
          <TerminalIcon sx={{ fontSize: 64, color: muiTheme.palette.text.disabled, marginBottom: 2 }} />
          <Typography color={muiTheme.palette.text.secondary} variant="h6" gutterBottom>
            No terminals assigned
          </Typography>
          <Typography color={muiTheme.palette.text.disabled}>
            {userProfile?.role === 'Admin' 
              ? 'Click "Add Terminal" to assign your first terminal to this site'
              : 'Contact your administrator to assign terminals to this site'
            }
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', gap: 2 }}>
          {terminals.map((terminal) => (
            <TerminalCard key={terminal.id}>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" marginBottom={2}>
                  <Box>
                    <Typography
                      variant="h6"
                      color={muiTheme.palette.text.primary}
                      gutterBottom
                      sx={{ fontWeight: 'bold' }}
                    >
                      {terminal.name}
                    </Typography>
                    <Typography
                      variant="body2"
                      color={muiTheme.palette.text.secondary}
                      gutterBottom
                    >
                      {terminal.makeModel || terminal.terminalType}
                    </Typography>
                    <Typography
                      variant="body2"
                      color={muiTheme.palette.text.disabled}
                    >
                      S/N: {terminal.serialNumber}
                    </Typography>
                  </Box>
                  
                  <StatusChip
                    label={getTerminalDisplayText(terminal)}
                    terminalstatus={terminal.status}
                    size="small"
                  />
                </Box>

                {terminal.siteLocation?.description && (
                  <Box display="flex" alignItems="center" marginBottom={1}>
                    <LocationIcon sx={{ color: muiTheme.palette.text.disabled, fontSize: 16, marginRight: 1 }} />
                    <Typography variant="body2" color={muiTheme.palette.text.secondary}>
                      {terminal.siteLocation.description}
                    </Typography>
                  </Box>
                )}

                {/* Hardware vitals - only show if feature flag is enabled */}
                {showHardwareVitals && terminal.assignedUnitId && (
                  <Box display="flex" alignItems="center" gap={2} marginBottom={2}>
                    <Box display="flex" alignItems="center">
                      <BatteryIcon sx={{ color: muiTheme.palette.success.main, fontSize: 18, marginRight: 0.5 }} />
                      <Typography variant="body2" color={muiTheme.palette.text.secondary}>
                        85%
                      </Typography>
                    </Box>
                    
                    <Box display="flex" alignItems="center">
                      <SignalIcon sx={{ color: muiTheme.palette.success.main, fontSize: 18, marginRight: 0.5 }} />
                      <Typography variant="body2" color={muiTheme.palette.text.secondary}>
                        Good
                      </Typography>
                    </Box>
                  </Box>
                )}

                <Typography variant="body2" color={muiTheme.palette.text.disabled} marginBottom={2}>
                  Last scanned: {formatLastScanned(terminal.lastScannedAt)}
                </Typography>

                <Box display="flex" alignItems="center" gap={1} marginBottom={1}>
                  <Chip
                    icon={<ScheduleIcon />}
                    label={`${terminal.scanDefaults?.duration || 30}s scan`}
                    size="small"
                    variant="outlined"
                    sx={{
                      borderColor: muiTheme.palette.divider,
                      color: muiTheme.palette.text.secondary,
                    }}
                  />
                  
                  <Chip
                    label={`${terminal.scanDefaults?.sensitivity || 'medium'} sensitivity`}
                    size="small"
                    variant="outlined"
                    sx={{
                      borderColor: muiTheme.palette.divider,
                      color: muiTheme.palette.text.secondary,
                    }}
                  />
                </Box>

                {terminal.tags && terminal.tags.length > 0 && (
                  <Box display="flex" flexWrap="wrap" gap={0.5} marginBottom={2}>
                    {terminal.tags.slice(0, 3).map((tag) => (
                      <Chip
                        key={tag}
                        label={tag}
                        size="small"
                        sx={{
                          backgroundColor: muiTheme.palette.info.main,
                          color: muiTheme.palette.info.contrastText,
                          fontSize: '0.7rem',
                          height: 20,
                        }}
                      />
                    ))}
                    {terminal.tags.length > 3 && (
                      <Chip
                        label={`+${terminal.tags.length - 3}`}
                        size="small"
                        sx={{
                          backgroundColor: muiTheme.palette.text.disabled,
                          color: muiTheme.palette.background.paper,
                          fontSize: '0.7rem',
                          height: 20,
                        }}
                      />
                    )}
                  </Box>
                )}

                {userProfile?.role === 'Admin' && (
                  <Box display="flex" justifyContent="flex-end" marginTop={2}>
                    <ActionButton size="small" onClick={() => openEditModal(terminal)} title="Edit Terminal">
                      <EditIcon fontSize="small" />
                    </ActionButton>
                    <ActionButton 
                      size="small" 
                      onClick={() => openDeleteDialog(terminal)} 
                      title="Delete Terminal"
                      sx={{
                        '&:hover': {
                          backgroundColor: muiTheme.palette.error.main,
                          color: muiTheme.palette.error.contrastText,
                        },
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </ActionButton>
                  </Box>
                )}
              </CardContent>
            </TerminalCard>
          ))}
        </Box>
      )}

      {/* Add/Edit Terminal Modal */}
      <AddTerminalModal
        open={addModalOpen}
        onClose={closeModal}
        onSave={editingTerminal ? handleEditTerminal : handleAddTerminal}
        siteId={siteId}
        editingTerminal={editingTerminal}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        PaperProps={{
          sx: {
            background: 'rgba(255, 255, 255, 0.03)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            border: '1px solid rgba(34, 211, 238, 0.25)',
            borderTop: '1px solid rgba(34, 211, 238, 0.5)',
            borderLeft: '1px solid rgba(34, 211, 238, 0.4)',
            borderRadius: '20px',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5), inset 0 1px 1px rgba(255, 255, 255, 0.05), inset 0 0 20px rgba(34, 211, 238, 0.05), 0 0 15px rgba(34, 211, 238, 0.1)',
          },
        }}
      >
        <DialogTitle
          sx={{
            fontFamily: "'Inter', sans-serif",
            color: '#ffffff',
            fontSize: '15px',
            fontWeight: 700,
            letterSpacing: '0.3em',
            textTransform: 'uppercase',
            padding: '20px 24px',
            borderBottom: '1px solid rgba(34, 211, 238, 0.2)',
            backgroundColor: 'transparent',
          }}
        >
          Delete Terminal
        </DialogTitle>
        <DialogContent sx={{ backgroundColor: 'transparent', padding: '24px' }}>
          <Typography color="text.primary">
            Are you sure you want to delete "{terminalToDelete?.name}" 
            (S/N: {terminalToDelete?.serialNumber})? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ padding: '16px 24px', gap: 1, borderTop: '1px solid rgba(34, 211, 238, 0.2)', backgroundColor: 'transparent' }}>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            variant="outlined"
            sx={{
              color: '#ffffff',
              border: '1px solid rgba(255, 255, 255, 0.15)',
              background: 'rgba(255, 255, 255, 0.1)',
              borderRadius: '50px',
              fontWeight: 'bold',
              px: 2,
              '&:hover': { background: 'rgba(255, 255, 255, 0.2)', borderColor: 'rgba(255, 255, 255, 0.25)' },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteTerminal}
            variant="outlined"
            sx={{
              color: '#ffffff',
              border: '1px solid rgba(244, 67, 54, 0.5)',
              background: 'rgba(244, 67, 54, 0.15)',
              borderRadius: '50px',
              fontWeight: 'bold',
              px: 2,
              '&:hover': { background: 'rgba(244, 67, 54, 0.3)', borderColor: 'rgba(244, 67, 54, 0.7)' },
            }}
          >
            Delete Terminal
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TerminalManagement;
